import React, {
  PropsWithChildren,
  createContext,
  useReducer,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useEffectOnce } from "react-use";

import Cookies from "js-cookie";

import { TEMPORARY_isNextAuthLocalStorage } from "@/lib/localstorages";

import { useRepeatedAutoLogin } from "./hooks";

type CommonState = {
  TEMPORARY_useNextAuth: boolean;
};

export type AuthenticatedState = CommonState & {
  isAuthenticated: true;
  userId: string;
};

export type UnauthenticatedState = CommonState & {
  isAuthenticated: false;
};

type State = AuthenticatedState | UnauthenticatedState;

const initialState: State = {
  isAuthenticated: false, //!!Cookies.get("login"),
  TEMPORARY_useNextAuth: false,
};

type Action =
  | {
      type: "LOGIN_OUT";
    }
  | { type: "LOGIN_IN"; payload: { userId: string } }
  | {
      type: "TEMPORARY_TOGGLE_USE_NEXT_AUTH";
      payload?: boolean;
    };

type Reducer = (state: State, action: Action) => State;

const reducer: Reducer = (state, action) => {
  switch (action.type) {
    case "LOGIN_IN":
      return { ...state, isAuthenticated: true, userId: action.payload.userId };
    case "LOGIN_OUT":
      return { ...state, isAuthenticated: false, userId: undefined };
    case "TEMPORARY_TOGGLE_USE_NEXT_AUTH":
      return {
        ...state,
        TEMPORARY_useNextAuth: action.payload ?? !state.TEMPORARY_useNextAuth,
      };
    default:
      return state;
  }
};

const useCreateAuthContextValue = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const logIn = useCallback((userId: string | number) => {
    dispatch({ type: "LOGIN_IN", payload: { userId: String(userId) } });
  }, []);

  const logOut = useCallback(() => {
    dispatch({ type: "LOGIN_OUT" });
  }, []);

  const TEMPORARY_toggleNextAuth = useCallback(
    (value?: boolean) => {
      TEMPORARY_isNextAuthLocalStorage.set(
        value ?? !state.TEMPORARY_useNextAuth,
      );
      dispatch({ type: "TEMPORARY_TOGGLE_USE_NEXT_AUTH", payload: value });
    },
    [state.TEMPORARY_useNextAuth],
  );

  const memoizedState = useMemo(() => state, [state]);

  return useMemo(
    () => ({
      ...memoizedState,
      logIn,
      logOut,
      TEMPORARY_toggleNextAuth,
    }),
    [TEMPORARY_toggleNextAuth, logIn, logOut, memoizedState],
  );
};

type AuthContextValue = State & {
  TEMPORARY_toggleNextAuth: (value?: boolean) => void;
};

export const AuthContext = createContext<AuthContextValue | null>(null);

type PropTypes = PropsWithChildren & {
  defaultIsAuthenticated?: boolean;
};

export const AuthContextProvider: React.FC<PropTypes> = ({
  children,
  defaultIsAuthenticated,
}) => {
  const { logIn, logOut, ...rest } = useCreateAuthContextValue();

  const [userId] = useState(Cookies.get("rlogin"));

  useEffect(() => {
    if (userId && !rest.isAuthenticated) {
      logIn(userId);
    }

    if (!userId && rest.isAuthenticated) {
      logOut();
    }
  }, [userId, logIn, logOut, rest]);

  useEffectOnce(() => {
    const isNextAuth = TEMPORARY_isNextAuthLocalStorage.get();
    rest.TEMPORARY_toggleNextAuth(isNextAuth ?? false);
  });

  useRepeatedAutoLogin(rest.isAuthenticated && rest.TEMPORARY_useNextAuth);

  return (
    <AuthContext.Provider
      value={{
        ...rest,
        ...(defaultIsAuthenticated && rest.TEMPORARY_useNextAuth && userId
          ? { isAuthenticated: true, userId }
          : { isAuthenticated: false }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuthContext must be used within an AuthContextProvider",
    );
  }

  return context;
};
